import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Users,
  Package,
  Download,
  RefreshCw,
} from 'lucide-react';
import RevenueChart from '../../components/Analytics/RevenueChart';
import OrdersChart from '../../components/Analytics/OrdersChart';
import ProductPerformanceChart from '../../components/Analytics/ProductPerformanceChart';
import AcquisitionChart from '../../components/Analytics/AcquisitionChart';
import {
  getAnalyticsSummary,
  getRevenueAnalytics,
  getOrdersAnalytics,
  getProductAnalytics,
  getAcquisitionAnalytics,
  getGeographicAnalytics,
  getRealTimeAnalytics,
  exportAnalyticsData,
} from '../../services/analyticsApi';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [granularity, setGranularity] = useState('daily');
  const [chartType, setChartType] = useState('line');

  const [summary, setSummary] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [ordersData, setOrdersData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [acquisitionData, setAcquisitionData] = useState([]);
  const [, setGeographicData] = useState([]);
  const [, setRealTimeData] = useState(null);

  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        summaryResult,
        revenueResult,
        ordersResult,
        productResult,
        acquisitionResult,
        geographicResult,
        realTimeResult,
      ] = await Promise.all([
        getAnalyticsSummary(timeRange),
        getRevenueAnalytics(timeRange, granularity),
        getOrdersAnalytics(timeRange, granularity),
        getProductAnalytics(),
        getAcquisitionAnalytics(),
        getGeographicAnalytics(),
        getRealTimeAnalytics(),
      ]);

      setSummary(summaryResult);
      setRevenueData(revenueResult.data);
      setOrdersData(ordersResult.data);
      setProductData(productResult.categories);
      setAcquisitionData(acquisitionResult.channels);
      setGeographicData(geographicResult.countries);
      setRealTimeData(realTimeResult);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange, granularity]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const realTimeResult = await getRealTimeAnalytics();
        setRealTimeData(realTimeResult);
      } catch (error) {
        console.error('Error loading real-time data:', error);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleExport = async (format = 'json') => {
    try {
      const result = await exportAnalyticsData(format, timeRange);
      const link = document.createElement('a');
      link.href = result.url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const getGrowthIcon = (growth) => {
    return growth >= 0 ? (
      <TrendingUp className="h-4 w-4 text-green-500" />
    ) : (
      <TrendingDown className="h-4 w-4 text-red-500" />
    );
  };

  const getGrowthColor = (growth) => {
    return growth >= 0 ? 'text-green-600' : 'text-red-600';
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Analytics Dashboard
          </h1>
          <p className="text-gray-600">
            Comprehensive insights into your business performance
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => loadAnalyticsData()}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => handleExport('json')}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row md:gap-6 gap-3 items-start justify-start md:items-center md:justify-between md:space-x-6">
          <div className="flex items-center justify-between md:justify-normal w-full space-x-2">
            <span className="text-sm font-medium text-gray-700">
              Time Range:
            </span>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>
          <div className="flex items-center justify-between md:justify-normal w-full space-x-2">
            <span className="text-sm font-medium text-gray-700">
              Granularity:
            </span>
            <select
              value={granularity}
              onChange={(e) => setGranularity(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="flex items-center justify-between md:justify-normal w-full space-x-2">
            <span className="text-sm font-medium text-gray-700">
              Chart Type:
            </span>
            <select
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="line">Line</option>
              <option value="bar">Bar</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary?.totalRevenue || 0)}
              </p>
              <div className="flex items-center mt-2">
                {getGrowthIcon(summary?.revenueGrowth || 0)}
                <span
                  className={`ml-1 text-sm font-medium ${getGrowthColor(
                    summary?.revenueGrowth || 0
                  )}`}
                >
                  {summary?.revenueGrowth > 0 ? '+' : ''}
                  {summary?.revenueGrowth?.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(summary?.totalOrders || 0)}
              </p>
              <div className="flex items-center mt-2">
                {getGrowthIcon(summary?.ordersGrowth || 0)}
                <span
                  className={`ml-1 text-sm font-medium ${getGrowthColor(
                    summary?.ordersGrowth || 0
                  )}`}
                >
                  {summary?.ordersGrowth > 0 ? '+' : ''}
                  {summary?.ordersGrowth?.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <ShoppingCart className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Customers
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(summary?.totalCustomers || 0)}
              </p>
              <div className="flex items-center mt-2">
                {getGrowthIcon(summary?.customersGrowth || 0)}
                <span
                  className={`ml-1 text-sm font-medium ${getGrowthColor(
                    summary?.customersGrowth || 0
                  )}`}
                >
                  {summary?.customersGrowth > 0 ? '+' : ''}
                  {summary?.customersGrowth?.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Avg Order Value
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary?.averageOrderValue || 0)}
              </p>
              <div className="flex items-center mt-2">
                <span className="ml-1 text-sm text-gray-500">Per order</span>
              </div>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <Package className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Revenue Trend
            </h3>
            <div className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              <span className="text-sm text-gray-500">
                {formatCurrency(
                  revenueData.reduce((sum, item) => sum + item.revenue, 0)
                )}
              </span>
            </div>
          </div>
          <RevenueChart
            data={revenueData}
            type={chartType}
            granularity={granularity}
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Orders Trend
            </h3>
            <div className="flex items-center space-x-2">
              <ShoppingCart className="h-5 w-5 text-green-600" />
              <span className="text-sm text-gray-500">
                {formatNumber(
                  ordersData.reduce((sum, item) => sum + item.orders, 0)
                )}
              </span>
            </div>
          </div>
          <OrdersChart
            data={ordersData}
            type={chartType}
            granularity={granularity}
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Product Performance
            </h3>
            <div className="flex items-center space-x-2">
              <Package className="h-5 w-5 text-orange-600" />
              <span className="text-sm text-gray-500">By Category</span>
            </div>
          </div>
          <ProductPerformanceChart data={productData} />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Customer Acquisition
            </h3>
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-purple-600" />
              <span className="text-sm text-gray-500">By Channel</span>
            </div>
          </div>
          <AcquisitionChart data={acquisitionData} />
        </div>
      </div>

      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Geographic Performance
          </h3>
          <span className="text-sm text-gray-500">Top Countries</span>
        </div>
        <GeographicChart data={geographicData} />
      </div> */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Top Products
          </h4>
          <div className="space-y-3">
            {productData.slice(0, 5).map((product, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {product.category}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(product.revenue)}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      product.growth >= 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {product.growth > 0 ? '+' : ''}
                    {product.growth.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Acquisition Channels
          </h4>
          <div className="space-y-3">
            {acquisitionData.slice(0, 5).map((channel, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{channel.channel}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">
                    {channel.customers}
                  </span>
                  <span className="text-xs text-gray-500">
                    ROI: {channel.roi}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Quick Stats
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Conversion Rate</span>
              <span className="text-sm font-medium text-gray-900">3.2%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Return Rate</span>
              <span className="text-sm font-medium text-gray-900">1.8%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg Session</span>
              <span className="text-sm font-medium text-gray-900">4m 32s</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Bounce Rate</span>
              <span className="text-sm font-medium text-gray-900">42.1%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
